import { defineStore } from "pinia";
import _ from "lodash";
import dayjs from "dayjs";
import { child, onChildAdded, onChildChanged, onChildRemoved, set } from "firebase/database";
import { ref, watch } from "vue";
import { bucket, isOfflineBoot, offlineBucketNames } from "~/services/storage/offlineBuckets";
import { loadSingleRow, waitForFirstRTDBSettle, isMainCashierRuntime } from "~/services/bootstrap/layerBHelpers";

// Same DB as pos.soldout, but each store owns its own row:
//   key `soldout`   → owned by pos.soldout
//   key `splitBill` → owned here
const SPLIT_BILL_KEY = "splitBill"

function getRuntimeBucket() {
  const sid = useAuthStore().storeId
  if (!sid) return null
  return bucket(offlineBucketNames.runtime(String(sid)))
}

export const usePosBillsNumberStore = defineStore("bill-number", () => {
  const loaded = ref(false)
  const uuidItemToBillNumber = ref<{ [uuidItem: string]: number }>({})


  const nonce = ref(dayjs().valueOf())


  const fetchSnapshots = () => {

    const billNumberRef = useRealtimeDatabase().billNumberRef()


    onChildAdded(billNumberRef, (data) => {
      uuidItemToBillNumber.value[data.key] = data.val() || {}
    });

    onChildChanged(billNumberRef, (snapshot) => {
      uuidItemToBillNumber.value[snapshot.key] = snapshot.val() || {}
    })

    onChildRemoved(billNumberRef, (data) => {
      uuidItemToBillNumber.value[data.key] = null
    });
    loaded.value = true


  }

  const persistToIDB = _.debounce(() => {
    // Only the Main Cashier owns the runtime IDB mirror — skip on
    // terminal / 2nd cashier / readonly.
    if (!isMainCashierRuntime()) return
    const b = getRuntimeBucket()
    if (!b) return
    void b.set(SPLIT_BILL_KEY, uuidItemToBillNumber.value)
  }, 300)

  async function bootOnline() {
    fetchSnapshots()
    watch(uuidItemToBillNumber, () => persistToIDB(), { deep: true })
    await waitForFirstRTDBSettle(useRealtimeDatabase().billNumberRef())
    loaded.value = true
  }

  async function bootOffline() {
    const cached = await loadSingleRow<Record<string, number>>(getRuntimeBucket(), SPLIT_BILL_KEY)
    if (cached) uuidItemToBillNumber.value = cached
    loaded.value = true
    fetchSnapshots()
    watch(uuidItemToBillNumber, () => persistToIDB(), { deep: true })
  }

  async function init() {
    if (isOfflineBoot()) return bootOffline()
    return bootOnline()
  }

  function setItem(uuidItem: string, billNumber: number) {
    const billNumberRef = useRealtimeDatabase().billNumberRef()
    set(child(billNumberRef, uuidItem), billNumber)
  }

  function removeItem(uuidItem: string) {
    const billNumberRef = useRealtimeDatabase().billNumberRef()
    set(child(billNumberRef, uuidItem), null)
  }


  return {
    name: "billsNumber",
    init,
    bootOnline,
    bootOffline,
    loaded,
    nonce,
    uuidItemToBillNumber,
    setItem,
    removeItem,
  };
});



